"use client";

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useAuthStore } from "@/lib/auth/store";
import {
  type CommunicationsChange,
  emitCommunicationsChanged,
  setCommunicationsSseConnected,
} from "./communicationsSseState";

/** Random 0–2s so simultaneous clients don't hit the API in the same tick. */
const FLUSH_JITTER_MS = 2_000;

// Module scope, not a ref: MarketFinderNavBadge sits behind a collapse
// ternary in Sidebar.tsx that swaps between a <Link> and a <div> at the same
// tree position, so toggling the sidebar unmounts and remounts this hook —
// a ref would reset right along with it, and an effect re-run on token
// refresh reset a plain local before that. Module scope tracks "this tab has
// had a live stream at some point", so the reconnect that follows either one
// still gets its catch-up (see onopen below).
let hasConnectedInTab = false;

/**
 * Live communication updates over SSE (GIQS-162). Subscribes to the backend
 * `changed` hint stream, then — after a short randomized delay that also
 * coalesces event bursts — invalidates the communications-summary query and
 * notifies mounted conversation views (scoped by application_id) so
 * badges/grid/threads refresh within seconds instead of waiting for the 60s
 * poll (which stays on as a fallback for reconnect gaps).
 *
 * Hits the backend directly (not the Next proxy) — SSE needs an unbuffered
 * stream and the Bearer header, both of which fetchEventSource provides.
 * Mount once (done in MarketFinderNavBadge, which lives in the sidebar).
 */
export function useCommunicationsEvents() {
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((state) => state.accessToken);

  useEffect(() => {
    if (!accessToken) return;

    const baseUrl =
      process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001";
    const controller = new AbortController();

    // Burst coalescing with jitter: changes accumulate until the flush timer
    // fires, then one summary refetch + one notification per application.
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    const pendingChanges = new Map<number | null, CommunicationsChange>();

    const flush = () => {
      flushTimer = null;
      const changes = [...pendingChanges.values()];
      pendingChanges.clear();
      if (changes.length === 0) return;
      queryClient.invalidateQueries({
        queryKey: ["market-finder-communications-summary"],
      });
      changes.forEach(emitCommunicationsChanged);
    };

    const scheduleChange = (change: CommunicationsChange) => {
      pendingChanges.set(change.applicationId, change);
      if (flushTimer === null) {
        flushTimer = setTimeout(flush, Math.random() * FLUSH_JITTER_MS);
      }
    };

    fetchEventSource(`${baseUrl}/api/v1/market-finder/communications/events`, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${accessToken}` },
      // Hidden tabs render nothing — let the library close the stream on
      // visibilitychange (server load drops) and reconnect when visible;
      // the onopen catch-up refetch covers everything missed while hidden.
      openWhenHidden: false,
      onopen: async (response) => {
        setCommunicationsSseConnected(response.ok);
        // Refetch on REconnect covers anything missed while disconnected
        // (null applicationId = unknown scope). Only the first connect of
        // this tab is skipped — initial queries are already in flight, and
        // emitting here would turn flaky-network reconnect loops into reload
        // storms.
        if (response.ok && hasConnectedInTab) {
          scheduleChange({ applicationId: null });
        }
        hasConnectedInTab = hasConnectedInTab || response.ok;
      },
      onclose: () => {
        // A server/proxy close is not the end: throwing routes through
        // onerror, which retries with backoff. Returning normally would
        // permanently stop live updates for this mount.
        setCommunicationsSseConnected(false);
        throw new Error("communications stream closed");
      },
      onmessage: (event) => {
        if (event.event !== "changed") return;
        let applicationId: number | null = null;
        try {
          applicationId = JSON.parse(event.data)?.application_id ?? null;
        } catch {
          // Malformed payload — treat as unscoped.
        }
        scheduleChange({ applicationId });
      },
      onerror: () => {
        // Returning nothing lets fetchEventSource retry with backoff; a
        // thrown error would stop the stream permanently. 401s resolve on
        // the next token refresh because accessToken re-runs the effect.
        setCommunicationsSseConnected(false);
      },
    }).catch(() => {
      // Aborted on unmount or fatal — polling fallback still covers updates.
      setCommunicationsSseConnected(false);
    });

    return () => {
      setCommunicationsSseConnected(false);
      if (flushTimer !== null) clearTimeout(flushTimer);
      controller.abort();
    };
  }, [accessToken, queryClient]);
}
